import * as http from '@/model/http'
import {
  arrayToObject,
  setHistory,
  setEntities,
  setEntity,
  getCachedEntities,
  getCachedEntity,
  getEntitiesByUrl,
  getEntityByUrl
} from '@/model/utils'
import {
  Skill,
  SkillCategoryList,
  SkillMemoHistory,
  UserSkillAssessment
} from './schema'
import { FetchOptions } from '@/model/types'

const fetchSkills = async (args?: {
  options?: FetchOptions
}): Promise<void> => {
  const url = 'skills'
  const cached = getCachedEntities('Skill', url, args?.options)
  if (cached) return
  const { data }: { data: Skill[] } = await http.get('skills')
  const skillIds = data.map((item) => {
    return `${item.id}`
  })
  // cache entity and history if needed
  if (!args?.options?.disableCache) {
    setHistory(url, skillIds, args?.options)
    setEntities('Skill', arrayToObject(data, 'id'))
  }
}

const getSkills = (): Skill[] => {
  return getEntitiesByUrl('Skill', 'skills')
}

const fetchSkillCategories = async (args?: { options?: FetchOptions }) => {
  const url = 'skill-categories'
  const cached = getCachedEntity('SkillCategoryList', url, args?.options)

  if (cached) return
  const { data }: { data: SkillCategoryList } = await http.get(
    'skill-categories'
  )

  // cache entity and history if needed
  if (!args?.options?.disableCache) {
    // use url as key, because the response of this api does not have a key to identify itself
    const key = url
    setHistory(url, [key], args?.options)
    setEntity('SkillCategoryList', data, key)
  }
}

const getSkillCategories = (): SkillCategoryList | undefined => {
  return getEntityByUrl('SkillCategoryList', 'skill-categories')
}

const getUserSkillAssessment = async (): Promise<UserSkillAssessment> => {
  const { data } = await http.get('/skill-assessment')
  return data
}

const fetchSkillMemoHistories = async (args?: {
  options?: FetchOptions
}): Promise<void> => {
  const url = 'skill-memo-histories'
  const cached = getCachedEntities('SkillMemoHistory', url, args?.options)
  if (cached) return
  const { data }: { data: SkillMemoHistory[] } = await http.get(url)
  const skillIds = data.map((item) => {
    return `${item.skillId}`
  })
  // cache entity and history if needed
  if (!args?.options?.disableCache) {
    setHistory(url, skillIds, args?.options)
    setEntities('SkillMemoHistory', arrayToObject(data, 'skillId'))
  }
}

const upsertSkillMemoHistory = async (params: {
  skillId: number
  text: string
}): Promise<void> => {
  const url = `skill-memo-histories`
  await http.post(url, {
    skill_id: params.skillId,
    text: params.text
  })

  // refetch skill memo histories
  await fetchSkillMemoHistories({ options: { ignoreCache: true } })
}

const getSkillMemoHistories = (): SkillMemoHistory[] => {
  return getEntitiesByUrl('SkillMemoHistory', 'skill-memo-histories')
}

export default {
  fetchSkills,
  getSkills,
  fetchSkillCategories,
  getSkillCategories,
  getUserSkillAssessment,
  fetchSkillMemoHistories,
  getSkillMemoHistories,
  upsertSkillMemoHistory
}
